import React, { useState } from 'react';
import { Footer, GradientHero, Card, SEO, breadcrumbSchema } from '@coupling/ui';
import { ExclamationCircleIcon, CheckCircleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@apollo/client';
import { SUBMIT_SALES_LEAD, SalesLeadInput, SubmitSalesLeadResponse } from '../graphql/salesMutations';
import Header from '../components/Header';
import { useBooking } from '../components/BookingProvider';
import { seoConfig } from '../config/seo';
import {
  bonusVoucherCopy,
  demoTopics,
  primaryDemoCta,
} from '../content/siteMessaging';

const expectedSizeOptions = [
  { value: '', label: 'Select expected size...' },
  { value: '<100', label: 'Less than 100 members' },
  { value: '100-500', label: '100-500 members' },
  { value: '500-1000', label: '500-1,000 members' },
  { value: '1000+', label: '1,000+ members' },
];

const neighborhoodOptions = [
  { value: '', label: 'Select your neighborhood...' },
  { value: 'west-asheville', label: 'West Asheville / Haywood Road' },
  { value: 'south-slope', label: 'South Slope' },
  { value: 'river-arts', label: 'River Arts District' },
  { value: 'north-asheville', label: 'North Asheville' },
  { value: 'downtown', label: 'Downtown Asheville' },
  { value: 'black-mountain', label: 'Black Mountain / Swannanoa' },
  { value: 'weaverville', label: 'Weaverville' },
  { value: 'brevard', label: 'Brevard / Transylvania County' },
  { value: 'hendersonville', label: 'Hendersonville' },
  { value: 'other-wnc', label: 'Other WNC area' },
];

export const Contact: React.FC = () => {
  const booking = useBooking();
  const [formData, setFormData] = useState<SalesLeadInput & { neighborhood: string }>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    expectedSize: '',
    message: '',
    neighborhood: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [submitSalesLead, { loading }] = useMutation<SubmitSalesLeadResponse>(SUBMIT_SALES_LEAD, {
    onCompleted: (data) => {
      if (data.submitSalesLead.success) {
        setSubmitted(true);
        setError(null);
      } else {
        setError(data.submitSalesLead.message || 'Something went wrong. Please try again.');
      }
    },
    onError: (err) => {
      setError(err.message || 'Unable to submit your request. Please try again or email us directly.');
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.businessName.trim()) {
      setError('Please enter your business name');
      return;
    }
    if (!formData.contactName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const neighborhoodLabel = neighborhoodOptions.find(n => n.value === formData.neighborhood)?.label || '';
    const regionTag = `[Source: Coupling WNC Contact Page]\n[Region: Western North Carolina${neighborhoodLabel ? ` - ${neighborhoodLabel}` : ''}]`;
    const messageWithRegion = formData.message?.trim()
      ? `${formData.message.trim()}\n\n${regionTag}`
      : regionTag;

    await submitSalesLead({
      variables: {
        input: {
          businessName: formData.businessName.trim(),
          contactName: formData.contactName.trim(),
          email: formData.email.trim(),
          phone: formData.phone?.trim() || undefined,
          expectedSize: formData.expectedSize || undefined,
          message: messageWithRegion,
        },
      },
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Book a demo or contact Coupling WNC - ${seoConfig.siteName}`}
        description={`Book a Coupling WNC demo, ask questions, and see how ${demoTopics} would fit your current Western North Carolina shop.`}
        canonical={`${seoConfig.siteUrl}/contact`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/contact`}
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          { name: 'Contact', url: `${seoConfig.siteUrl}/contact` },
        ])}
      />
      <Header />

      <GradientHero
        title="Book a demo or ask about your shop"
        description={`Merchants can book a demo, ask questions, and see how ${demoTopics} would work in their current WNC workflow.`}
      />

      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <Card className="p-8 text-center">
                  <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
                  <h3 className="text-2xl font-bold text-neutral-900 mb-2">
                    Welcome to the WNC network!
                  </h3>
                  <p className="text-lg text-neutral-600 mb-6">
                    Thank you for your interest in Coupling WNC. Our team will reach out to you
                    within 1-2 business days to get you started.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        businessName: '',
                        contactName: '',
                        email: '',
                        phone: '',
                        expectedSize: '',
                        message: '',
                        neighborhood: '',
                      });
                    }}
                    className="px-6 py-3 text-sm font-medium text-white bg-brand-primary rounded-full hover:bg-brand-primary-dark transition-colors"
                  >
                    Send Another Message
                  </button>
                </Card>
              ) : (
                <Card className="p-8">
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                    Get in Touch
                  </h2>
                  <p className="text-neutral-600 mb-6">
                    Tell us about your WNC business, book a demo, or ask how Coupling would handle rewards, vouchers, gift cards, and rollout questions in your current workflow.
                  </p>
                  <p className="text-sm font-medium text-brand-primary mb-6">
                    {bonusVoucherCopy}
                  </p>

                  {error && (
                    <div role="alert" className="flex items-center gap-2 p-3 mb-4 text-sm text-red-700 bg-red-50 rounded-lg">
                      <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="businessName" className="block text-sm font-medium text-neutral-700 mb-1">
                          Business Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="businessName"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                          placeholder="Your business name"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="contactName" className="block text-sm font-medium text-neutral-700 mb-1">
                          Your Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contactName"
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                          placeholder="Your name"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                          placeholder="you@business.com"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-1">
                          Phone <span className="text-neutral-400">(optional)</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                          placeholder="+1 (828) 555-1234"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="neighborhood" className="block text-sm font-medium text-neutral-700 mb-1">
                          Neighborhood / Area
                        </label>
                        <select
                          id="neighborhood"
                          name="neighborhood"
                          value={formData.neighborhood}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                        >
                          {neighborhoodOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="expectedSize" className="block text-sm font-medium text-neutral-700 mb-1">
                          Expected Community Size
                        </label>
                        <select
                          id="expectedSize"
                          name="expectedSize"
                          value={formData.expectedSize}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                        >
                          {expectedSizeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-1">
                        Message <span className="text-neutral-400">(optional)</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors resize-none"
                        placeholder="Tell us about your business and what you're looking for..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 text-sm font-medium text-white bg-brand-primary rounded-full hover:bg-brand-primary-dark transition-all shadow-lg hover:shadow-brand-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </form>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Highest-visibility option: instant scheduled call. */}
              <Card className="p-6 border-brand-primary/30 bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
                <div className="flex items-start gap-3 mb-3">
                  <CalendarDaysIcon className="h-6 w-6 text-brand-primary flex-shrink-0 mt-0.5" />
                  <h3 className="text-lg font-semibold text-neutral-900">
                    Want to talk now?
                  </h3>
                </div>
                <p className="text-neutral-600 mb-4">
                  {primaryDemoCta} to see how Coupling WNC would fit your shop, or start with a quick intro call if you want to ask a few questions first.
                </p>
                <p className="text-sm font-medium text-brand-primary mb-4">
                  Ask how merchant rewards and vouchers can support your launch.
                </p>
                <button
                  type="button"
                  onClick={() => booking.open('intro')}
                  className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 text-sm font-semibold text-white bg-brand-primary rounded-full hover:bg-brand-primary-dark transition-all shadow-md hover:shadow-brand-primary/25"
                >
                  <CalendarDaysIcon className="h-4 w-4" />
                  Book a 15-min call
                </button>
                <button
                  type="button"
                  onClick={() => booking.open('demo')}
                  className="inline-flex items-center justify-center gap-2 w-full mt-2 px-5 py-2.5 text-sm font-medium text-brand-primary border-2 border-brand-primary/30 bg-white rounded-full hover:bg-brand-primary/5 transition-colors"
                >
                  {primaryDemoCta}
                </button>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-3">Email Us Directly</h3>
                <p className="text-neutral-600 mb-3">
                  Prefer email? Reach us anytime at:
                </p>
                <a
                  href="mailto:hello@cplng.org?subject=Coupling%20WNC%20Inquiry"
                  className="text-brand-primary hover:text-brand-primary-dark font-medium hover:underline"
                >
                  hello@cplng.org
                </a>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-3">Large organizations or custom integrations</h3>
                <p className="text-neutral-600 mb-3">
                  Large organization or custom integration needs? Let's talk about a tailored plan for your WNC operation.
                </p>
                <a
                  href="mailto:hello@cplng.org?subject=Custom%20Integration%20Inquiry%20-%20WNC"
                  className="text-brand-primary hover:text-brand-primary-dark font-medium hover:underline"
                >
                  Custom integration inquiries
                </a>
              </Card>

              <Card className="p-6 bg-bgSubtle">
                <h3 className="text-lg font-semibold text-neutral-900 mb-3">Part of the Coupling Network</h3>
                <p className="text-neutral-600 mb-3">
                  Coupling WNC is a regional node in the larger Coupling open-source network. Learn more about the broader platform after you see the merchant workflow.
                </p>
                <a
                  href="https://cplng.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-primary-dark font-medium hover:underline"
                >
                  Visit cplng.org
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
